import React, { useState } from 'react';
import { Search, Tag, Loader2, AlertCircle } from 'lucide-react';
import { useCollections } from '../hooks/useCollections';

interface CollectionSelectorProps {
  onSelect: (collection: any) => void;
  onClose: () => void;
}

export function CollectionSelector({ onSelect, onClose }: CollectionSelectorProps) {
  const { collections, loading, error } = useCollections();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Get unique tags across all collections
  const allTags = Array.from(
    new Set(collections.flatMap(c => c.tags))
  ).sort();

  const filteredCollections = collections.filter(collection => {
    // If there are selected tags, collection must include ALL selected tags
    if (selectedTags.length > 0 && !selectedTags.every(tag => collection.tags.includes(tag))) {
      return false;
    }
    
    // If there's a search query, check title and content
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        collection.title.toLowerCase().includes(query) ||
        collection.content.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  if (loading) {
    return (
      <div className="collection-selector-form bg-white rounded-lg shadow-lg p-6 min-w-[400px]">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-electric" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-selector-form bg-white rounded-lg shadow-lg p-6 min-w-[400px]">
        <div className="flex items-center text-crimson mb-4">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
        <button
          onClick={onClose}
          className="px-4 py-2 text-slate hover:text-midnight"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="collection-selector-form bg-white rounded-lg shadow-lg p-6 min-w-[400px]">
      <h2 className="text-lg font-semibold text-midnight mb-4">Insert from Collections</h2>
      
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
          />
        </div>

        {allTags.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Tag className="h-4 w-4 text-slate" />
              <span className="text-sm font-medium text-slate">Filter by tags:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-electric text-white'
                      : 'bg-sand/20 text-slate hover:bg-sand/30'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="max-h-64 overflow-y-auto">
          {filteredCollections.length === 0 ? (
            <div className="text-center py-8 text-slate">
              No collections found
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCollections.map(collection => (
                <button
                  key={collection.id}
                  onClick={() => onSelect(collection)}
                  className="w-full text-left p-4 rounded-lg border border-sand hover:border-electric transition-colors"
                >
                  <h3 className="font-medium text-midnight mb-1">{collection.title}</h3>
                  {collection.description && (
                    <p className="text-sm text-slate mb-2">{collection.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {collection.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-electric/10 text-electric"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-sand/20">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate hover:text-midnight"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}